import React, { useState, createContext, useContext, useCallback } from 'react';
import { http, auth as api } from '~/api';
import config from '~/config';
import { auth } from '~/util';
import get from 'lodash/get';
import storage from '~/util/storage';

const authContext = createContext(null);

authContext.displayName = 'AuthContext';

/**
 * 
 * @typedef AuthContext
 * @property {User} user The logged in User
 * @property {string} token The JWT for the logged in User
 * @property {(username: string, password: string) => void} login Attempts to log in using the provided credentials
 * @property {() => void} logout Clears auth state and logs out the current User
 * @property {() => Promise} reloadUser Refreshes the user state
 * @property {(requiredRole: number) => boolean} hasRole Verifies the current User has an appropriate role
 * @property {(firmId: string) => boolean} isWithFirm verifies the current User is associated with the given firm
 * @property {boolean} isTryon Returns true if the current user has a Tryon role
 * @property {boolean} isLawFirm Returns true if the current user has a Law Firm role
 * @property {boolean} isSiteAdmin Returns true if the current user is a site admin
 * @property {() => any[]} loadAccessibleTitles Populates title dropdown for user pages
 */

/**
 * @typedef User
 * @property {string} id
 * @property {string} roleId
 * @property {string} username
 * @property {string} name
 * @property {string} email
 * @property {string} firmId
 * @property {string} firmName
 * @property {string} preferences
 */

/**
 * Hook for subcribing to the auth context
 *
 * @returns {AuthContext} The AuthContent
 */
export const useAuth = () => {
    return useContext(authContext);
};

export const usePreferences = () => {
    const { user, reloadUser } = useAuth();
    const [preferences, setPreferences] = useState(user.preferences || {});
    const [preferencesChanged, setPreferencesChanged] = useState(false);

    const updatePreferences = async newPreferences => {
        await api.updateUserPreferences(user.id, { preferences: newPreferences });
        setPreferences(newPreferences);
        setPreferencesChanged(true);
    };

    return { preferences, updatePreferences, preferencesChanged, reloadUser };
};

export const ProvideAuth = ({ children }) => {
    const auth = useProvideAuth();
    return (
        <authContext.Provider value={auth}>
            {children}
        </authContext.Provider>
    );
};

/**
 * Handles authentication state
 * @returns {AuthContext}
 */
function useProvideAuth() {
    const [state, setState] = useState(() => {
        const cachedToken = auth.getToken();

        if (cachedToken != null && auth.isTokenValid()) {
            // if (auth.isTokenExpired(cachedToken)) {
            //     auth.clearToken();
            //     return null;
            // }

            const user = getUserFromToken(cachedToken);
            return {
                token: cachedToken,
                user
            };
        }

        return null;
    });

    const set = (token = '') => {
        auth.setToken(token);
        const user = getUserFromToken(token);

        setState({
            token,
            user
        });
    };

    const clear = () => {
        auth.clearToken();
        clearCaches();
        setState(null);
    };

    const hasRole = useCallback((requiredRole) => {
        if (state.user.roleId) {
            return auth.checkRole(state.user.roleId, requiredRole);
        }
        return false;
    }, [state]);

    const isWithFirm = (firmId) => {
        return state.user.firmId == firmId;
    };

    const isTryon = auth.isTryon(get(state, 'user.roleId', null));
    const isLawFirm = auth.isLawFirm(get(state, 'user.roleId', null));
    const isSiteAdmin = auth.checkRole(get(state, 'user.roleId', null), auth.ROLES.ADMIN);

    const loadAccessibleTitles = () => {
        const titles = auth.TITLES.map(t => {
            return {
                label: t,
                value: t
            };
        });

        return state.user.roleId == auth.ROLES.FIRM_ADMIN ? titles.filter(t => t.value != 'Tryon Employee') : titles;
    };

    const login = async (username, password) => {
        const response = await http.post(
            `${config.urls.security}/login`,
            { username, password },
            { authHeader: false }
        );

        if (response.errored) {
            throw new Error(response.error);
        }

        set(response.token);
    };

    const reloadUser = async () => {
        const { id } = get(state, 'user', {});

        if (id == null) {
            logout();
            return;
        }

        const newUser = await api.getUserDetails(id);

        setState({
            ...state,
            user: {
                ...state.user,
                id,
                username: newUser.username,
                firstName: newUser.firstName,
                lastName: newUser.lastName,
                name: `${newUser.firstName} ${newUser.lastName}`,
                roleId: newUser.roleId,
                firmId: newUser.firmId,
                email: newUser.emailAddress,
                preferences: JSON.parse(newUser.preferences)
            }
        });
    };

    const logout = () =>
        clear();

    return {
        token: state ? state.token : null,
        user: state ? state.user : null,
        hasRole,
        isWithFirm,
        isTryon,
        isLawFirm,
        isSiteAdmin,
        loadAccessibleTitles,
        login,
        reloadUser,
        logout
    };
}

function getUserFromToken(token) {
    const decoded = auth.decode(token);
    return formatTokenPayload(decoded);
}

/**
 * Formats a decoded token payload into a @see {User} object
 * @param {Object} payload The decoded token payload
 * @returns {User}
 */
function formatTokenPayload(payload) {
    return {
        id: payload.sub,
        roleId: payload.roleId,
        firmId: payload.firmId,
        firmName: payload.firmName,
        username: payload.username,
        name: payload.name,
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        preferences: payload.preferences
    };
}


/**
 * HACK
 * Maybe not the best place to put this, but
 * at least there's a centralized point where 
 * you can easily see which caches are emptied on logout
 */
function clearCaches() {
    for (let key of storage.cacheKeys.logout) {
        storage.removeItem(key);
    }
}